/**
 * Pure ML-prediction sanitizer for /process. The V4 categorical model emits junk
 * for fields it can't predict — empty-list `$ $`, `[NULL]`/`[UNK]`, placeholder
 * `.`, and a few internal bookkeeping keys — all at meaningless (fluency)
 * confidence. These helpers drop that junk and decode the model's `$`-delimited
 * multi-value list encoding. See spec 2026-06-30-ai-prefilled-quality-cleanup.
 *
 * It also enforces two rules about what the model is ALLOWED to suggest. These
 * are not cleanup — they are policy, and they exist because predictions appear
 * inline in the ATLAS form with an "Apply All" button, one click away from a
 * customs declaration that someone signs.
 *
 * Measured on a packing list shipping to India, after the field-name fix that
 * made the model read the document at all: of 19 clearly-invented values, 17
 * still mapped to a form field. Among them a declarant EORI belonging to
 * someone else, another company's customs authorisation, a fabricated end-user
 * party (name, "Moscow", Switzerland, "." for street and postcode, and the TIN
 * "DE000000000000000" at 100% confidence).
 *
 * Raising the confidence threshold does not help: the worst of those came back
 * at 1.0.
 */

const ML_INTERNAL_KEYS = new Set([
  'delete_status', 'create_date', 'sent_identifier', 'intern_info',
]);

/**
 * RULE 1 — never predict WHO someone is.
 *
 * A model cannot know your counterparty. Party names, identification numbers
 * and addresses come from the document or from configuration; a guessed one is
 * an identity misstatement on a legal document, not a helpful default.
 */
const IDENTITY_PREFIXES = ['endverwer_', 'anmeld_', 'anmled_', 'versender_'];
const IDENTITY_KEYS = new Set([
  'h_ausfuhrer_id', 'h_subunternehmer_id', 'h_vert_des_anmeld_id',
  'empfng_identifikationsart',
]);

/**
 * RULE 2 — never predict what the user has already told us.
 *
 * These fields are owned by ATLAS configuration: the customs offices, the
 * authorised-exporter reference, the declaration type and participant
 * constellation. Suggesting a value here either overrides the user's own
 * setting or, when unset, offers a customs office lifted from training data.
 * Both are worse than leaving the field empty.
 */
const CONFIGURATION_OWNED_KEYS = new Set([
  'dienst_no', 'ausfuhzolllstelle_dienst', 'vorge_ausgang_dienst',
  'gestellungszollstelle', 'bewlg_zugelassener_aus',
  'art_der_anm_verfahren', 'kopf_art_der_aus', 'kopf_bet_konstell',
]);

/** A country code followed only by zeros — e.g. "DE000000000000000". Never a
 *  real identifier, and distinct from the legitimate single "0" used by
 *  containerIndikator and sicherheit. */
const ALL_ZERO_IDENTIFIER = /^[A-Za-z]{2}0+$/;

/** "2.0", "40.0", "11.0" — the model rendering an integer code as a float.
 *  Every ML-predicted field of this shape is a code whose ATLAS format is
 *  digits only, so the trailing ".0" makes the value invalid rather than
 *  merely untidy. */
const FLOAT_ARTIFACT = /^\d+\.0+$/;

/** "$DE$US$"→["DE","US"]; "DE,US"→["DE","US"]; "$ $"/""/null→[]. Handles the
 *  model's $-delimited list encoding and the comma form. */
export function parseMultiValue(value: unknown): string[] {
  const v = String(value ?? '').trim();
  if (!v) return [];
  const parts = v.includes('$') ? v.split('$') : v.split(',');
  return parts.map((s) => s.trim()).filter(Boolean);
}

/** True when the model must not offer this field at all, regardless of value. */
export function isSuppressedField(mlKey: string): boolean {
  if (!mlKey) return false;
  if (IDENTITY_KEYS.has(mlKey) || CONFIGURATION_OWNED_KEYS.has(mlKey)) return true;
  return IDENTITY_PREFIXES.some((p) => mlKey.startsWith(p));
}

/** True when an ML prediction is junk that must never surface in the UI. */
export function isJunkPrediction(mlKey: string, value: unknown): boolean {
  if (ML_INTERNAL_KEYS.has(mlKey)) return true;
  if (isSuppressedField(mlKey)) return true;
  // Compound values (the model echoes whole kopf sub-objects like `anmelder`).
  // The ML->ATLAS map is keyed on flat names (`ausfuh_tin`), so these can never
  // resolve to a form path; the caller's String(value) would render a chip
  // reading "[object Object]".
  if (value !== null && typeof value === 'object') return true;
  const v = String(value ?? '').trim();
  if (v === '' || v === '[NULL]' || v === '[UNK]' || v === '.') return true;
  if (v.startsWith('$') && v.replace(/[$\s]/g, '') === '') return true; // $ $ / $$ / $
  if (ALL_ZERO_IDENTIFIER.test(v)) return true;
  if (FLOAT_ARTIFACT.test(v)) return true;
  return false;
}

/**
 * Strip junk from a raw /predict/impute response.
 *
 * Applied at the single point where the response is cached, so every consumer
 * (the sidebar list, the inline per-field chips, "Apply All") sees the same
 * filtered set. Filtering at each call site instead lets a path be added later
 * that quietly bypasses the rules.
 */
export function sanitizeMlResponse<T extends Record<string, any>>(data: T): T {
  if (!data || typeof data !== 'object') return data;
  const clean = (obj: Record<string, any> | undefined | null) => {
    if (!obj || typeof obj !== 'object') return obj;
    const out: Record<string, any> = {};
    Object.entries(obj).forEach(([k, v]) => {
      if (!isJunkPrediction(k, v)) out[k] = v;
    });
    return out;
  };
  const result: Record<string, any> = { ...data };
  if (result.header) result.header = clean(result.header);
  if (Array.isArray(result.positions)) result.positions = result.positions.map(clean);
  if (result.position) result.position = clean(result.position);
  // Drop confidence entries whose field is gone, so nothing counts a
  // suppressed field toward the "high confidence" totals on Apply All.
  const scores = result.confidence_scores;
  if (scores && typeof scores === 'object') {
    const kept: Record<string, any> = {};
    Object.entries(scores).forEach(([key, v]) => {
      const field = String(key).split('.').slice(1).join('.') || key;
      if (!isJunkPrediction(field, 'x')) kept[key] = v;
    });
    result.confidence_scores = kept;
  }
  return result as T;
}
